import type { MatrixClient, Room } from 'matrix-js-sdk'

// Power-level helpers + permission gating for member management (invite, kick,
// ban, role changes). Everything reads the room's m.room.power_levels state.

interface PowerLevels {
  users?: Record<string, number>
  users_default?: number
  invite?: number
  kick?: number
  ban?: number
  events?: Record<string, number>
  events_default?: number
  state_default?: number
}

// Matrix spec defaults when a field is absent from m.room.power_levels.
const DEFAULT_INVITE = 0
const DEFAULT_KICK = 50
const DEFAULT_BAN = 50
const DEFAULT_STATE = 50

export const ROLES = [
  { label: 'Member', level: 0 },
  { label: 'Moderator', level: 50 },
  { label: 'Admin', level: 100 },
] as const

export function roleName(level: number): string | null {
  if (level >= 100) return 'Admin'
  if (level >= 50) return 'Moderator'
  return null
}

function powerLevels(room: Room): PowerLevels {
  return (room.currentState.getStateEvents('m.room.power_levels', '')?.getContent() as PowerLevels) ?? {}
}

export function userPowerLevel(room: Room, userId: string): number {
  const pl = powerLevels(room)
  return pl.users?.[userId] ?? pl.users_default ?? 0
}

export function myPowerLevel(client: MatrixClient, room: Room): number {
  const me = client.getUserId()
  return me ? userPowerLevel(room, me) : 0
}

/** Level required to change m.room.power_levels (i.e. to set anyone's role). */
function requiredToSetRoles(room: Room): number {
  const pl = powerLevels(room)
  return pl.events?.['m.room.power_levels'] ?? pl.state_default ?? DEFAULT_STATE
}

export function canInvite(client: MatrixClient, room: Room): boolean {
  return myPowerLevel(client, room) >= (powerLevels(room).invite ?? DEFAULT_INVITE)
}
export function canKick(client: MatrixClient, room: Room): boolean {
  return myPowerLevel(client, room) >= (powerLevels(room).kick ?? DEFAULT_KICK)
}
export function canBan(client: MatrixClient, room: Room): boolean {
  return myPowerLevel(client, room) >= (powerLevels(room).ban ?? DEFAULT_BAN)
}
export function canSetRoles(client: MatrixClient, room: Room): boolean {
  return myPowerLevel(client, room) >= requiredToSetRoles(room)
}

/**
 * Whether you outrank a target — required to act on them. You must have a
 * strictly higher power level (you can never kick/ban/demote a peer or someone
 * above you), and it can't be yourself.
 */
export function outranks(client: MatrixClient, room: Room, targetId: string): boolean {
  const me = client.getUserId()
  if (!me || me === targetId) return false
  return myPowerLevel(client, room) > userPowerLevel(room, targetId)
}

/** Whether you can set this target to `level` (can't grant at/above your own). */
export function canAssignRole(client: MatrixClient, room: Room, targetId: string, level: number): boolean {
  return canSetRoles(client, room) && outranks(client, room, targetId) && level < myPowerLevel(client, room)
}
